// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatMessageBubble.tsx
 * @input Uses React, StyleX, ChatMessageContext, theme tokens
 * @output Exports ChatMessageBubble component and ChatMessageBubbleProps
 * @position Styled content container — the actual "chat bubble" with sender-aware styling
 *
 * Reads sender from parent ChatMessage context to auto-style background.
 * Optional — not all message content needs bubble treatment.
 *
 * Usage guidance:
 * - If you use bubbles on one side (e.g. assistant), use them consistently
 *   for all messages on that side. Use `ghost` variant for content that
 *   needs alignment without a visual boundary.
 * - Put `name` on the first bubble in a message, `metadata` on the last.
 * - For unbubbled messages, use ChatMessage's `name` and `metadata`
 *   props instead.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Chat/index.ts (exports)
 * - /apps/storybook/stories/Chat.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/ChatMessageBubble/ (block examples)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typeScaleVars, typographyVars } from "../theme/tokens.stylex.js";
import { useChatMessageContext } from "./ChatContext.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  content: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    ks0D6T: "xl6temz",
    kaIpWk: "xashgol",
    kMv6JI: "x9ynric",
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    kHjlTd: "x1mzt3pk",
    kTgw9: "x13faqbe",
    $$css: true
  },
  radiusCompact: {
    kaIpWk: "x1hviunn",
    $$css: true
  },
  paddingCompact: {
    k8WAf4: "x8o8v82",
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  paddingBalanced: {
    k8WAf4: "x8o8v82",
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  paddingSpacious: {
    k8WAf4: "x1na6nto",
    kg3NbH: "xzz68wx",
    $$css: true
  },
  paddingBlockNone: {
    k8WAf4: "xt970qd",
    $$css: true
  },
  metadataPaddingCompact: {
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  metadataPaddingBalanced: {
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  metadataPaddingSpacious: {
    kg3NbH: "xzz68wx",
    $$css: true
  },
  metadataReducedGap: {
    keoZOQ: "x1c40v9y",
    $$css: true
  },
  headerReducedGap: {
    k1K539: "xdrfirk",
    $$css: true
  },
  nameRow: {
    kZKoxP: "x1grt7ep",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    $$css: true
  },
  alignEnd: {
    k9WMMc: "xp4054r",
    $$css: true
  },
  assistant: {
    kWkggS: "x17x4s8c",
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  user: {
    kWkggS: "x17x4s8c",
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  ghost: {
    kWkggS: "xjbqb8w",
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  groupFirstAssistant: {
    kVL7Gh: "xrfs2vl",
    $$css: true
  },
  groupMiddleAssistant: {
    krdFHd: "x3qb8m",
    kVL7Gh: "xrfs2vl",
    $$css: true
  },
  groupLastAssistant: {
    krdFHd: "x3qb8m",
    $$css: true
  },
  groupFirstUser: {
    kT0f0o: "x1whkvtz",
    $$css: true
  },
  groupMiddleUser: {
    kfmiAY: "x126per9",
    kT0f0o: "x1whkvtz",
    $$css: true
  },
  groupLastUser: {
    kfmiAY: "x126per9",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * Styled content container — the chat "bubble."
 *
 * Reads sender from parent ChatMessage context to auto-style background.
 * Use `group` prop for multi-bubble corner grouping.
 *
 * @example
 * ```
 * <ChatMessage sender="user">
 *   <ChatMessageBubble
 *     name="Cindy"
 *     metadata={<ChatMessageMetadata timestamp="2:30 PM" status="read" />}>
 *     Hey, how's it going?
 *   </ChatMessageBubble>
 * </ChatMessage>
 * ```
 */
export function ChatMessageBubble({
  children,
  variant = 'filled',
  name,
  metadata,
  group,
  xstyle,
  className,
  style: styleProp,
  'data-testid': testId,
  ref,
  ...rest
}) {
  const msgContext = useChatMessageContext();
  const sender = msgContext?.sender ?? 'assistant';
  const density = msgContext?.density ?? 'balanced';
  const paddingStyle = density === 'compact' ? styles.paddingCompact : density === 'spacious' ? styles.paddingSpacious : styles.paddingBalanced;
  const isUser = sender === 'user';
  const senderStyle = variant === 'ghost' ? styles.ghost : isUser ? styles.user : styles.assistant;
  const groupStyle = group === 'first' ? isUser ? styles.groupFirstUser : styles.groupFirstAssistant : group === 'middle' ? isUser ? styles.groupMiddleUser : styles.groupMiddleAssistant : group === 'last' ? isUser ? styles.groupLastUser : styles.groupLastAssistant : null;
  const metadataPaddingStyle = density === 'compact' ? styles.metadataPaddingCompact : density === 'spacious' ? styles.metadataPaddingSpacious : styles.metadataPaddingBalanced;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [name && /*#__PURE__*/_jsx("div", {
      "data-chat-name": true,
      ...stylex.props(metadataPaddingStyle, styles.nameRow, styles.headerReducedGap, isUser && styles.alignEnd),
      children: name
    }), /*#__PURE__*/_jsx("div", {
      ...rest,
      ref: ref,
      "data-testid": testId,
      ...mergeProps(themeProps('chat-message-bubble', {
        sender,
        variant,
        density
      }), stylex.props(styles.content, density === 'compact' && styles.radiusCompact, senderStyle, paddingStyle, variant === 'ghost' && styles.paddingBlockNone, groupStyle, xstyle), className, styleProp),
      children: children
    }), metadata && /*#__PURE__*/_jsx("div", {
      ...stylex.props(metadataPaddingStyle, styles.metadataReducedGap, isUser && styles.alignEnd),
      children: metadata
    })]
  });
}
ChatMessageBubble.displayName = 'ChatMessageBubble';